/* ==========================================================================
   nimble cares theme.js
   Loaded deferred. GSAP + ScrollTrigger + Lenis (if present) drive the premium
   motion layer; everything degrades to a clean, complete page without them.
   Blueprint §3.4 / §7.
   ========================================================================== */
(function () {
  'use strict';

  window.nimble = window.nimble || {};
  var cfg = window.nimble.settings || {};
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var saveData = navigator.connection && navigator.connection.saveData;
  var hasGSAP = typeof window.gsap !== 'undefined';
  var hasST = hasGSAP && typeof window.ScrollTrigger !== 'undefined';
  var hasLenis = typeof window.Lenis !== 'undefined';
  var motionOK = cfg.animations !== false && !reduceMotion;

  var EASE = 'cubic-bezier(0.22,1,0.36,1)';
  var GSAP_EASE = 'power3.out';

  document.addEventListener('DOMContentLoaded', init);

  /* --- Product-card image carousels --------------------------------------
     Delegated + registered immediately, so the arrows keep working on cards
     injected later (Section Rendering API, mega-menu tabs) and even if init()
     below throws. Steps by the slides' real offsetLeft rather than by
     clientWidth: clientWidth is rounded to an integer, so on fractional card
     widths (calc/clamp grids) a multiply-by-index drifts off the snap points
     and the scroll stalls part-way through the strip. */
  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('[data-pcard-nav]');
    if (!btn) return;
    e.preventDefault();
    e.stopPropagation();

    var media = btn.closest('.pcard__media');
    var track = media && media.querySelector('.pcard__carousel');
    if (!track) return;

    var slides = Array.prototype.filter.call(track.children, function (el) {
      return el.classList.contains('pcard__slide');
    });
    if (slides.length < 2) return;

    var current = 0;
    var closest = Infinity;
    for (var i = 0; i < slides.length; i++) {
      var gap = Math.abs(slides[i].offsetLeft - track.scrollLeft);
      if (gap < closest) { closest = gap; current = i; }
    }

    var step = btn.getAttribute('data-pcard-nav') === 'prev' ? -1 : 1;
    var target = Math.min(slides.length - 1, Math.max(0, current + step));
    track.scrollTo({ left: slides[target].offsetLeft, behavior: 'smooth' });
  }, true);

  /* Hide the arrows on cards whose strip can't actually scroll, so a card either
     has working arrows or none at all - a product with a single image has
     nothing to step through. Tracks with no width yet (cards in a display:none
     mega-menu panel) are left alone and re-measured on hover. */
  function syncCardArrows(media) {
    var track = media.querySelector('.pcard__carousel');
    var arrows = media.querySelectorAll('[data-pcard-nav]');
    if (!track || !arrows.length || !track.clientWidth) return;
    var scrollable = track.scrollWidth - track.clientWidth > 2;
    for (var i = 0; i < arrows.length; i++) { arrows[i].hidden = !scrollable; }
  }
  function syncAllCardArrows() {
    document.querySelectorAll('.pcard__media').forEach(syncCardArrows);
  }
  // Re-measure the card the pointer is actually on: that's the moment the arrows
  // are revealed, and it catches cards laid out after load (mega-menu tabs).
  document.addEventListener('pointerover', function (e) {
    var media = e.target.closest && e.target.closest('.pcard__media');
    if (media) syncCardArrows(media);
  });
  window.addEventListener('load', syncAllCardArrows);
  document.addEventListener('shopify:section:load', syncAllCardArrows);
  var arrowResizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(arrowResizeTimer);
    arrowResizeTimer = setTimeout(syncAllCardArrows, 150);
  });

  function init() {
    try {
      if (hasGSAP) { 
        window.gsap.registerPlugin(window.ScrollTrigger); 
        if (hasST) {
          window.ScrollTrigger.config({ ignoreMobileResize: true });
        }
      }
      
      var inEditor = window.Shopify && window.Shopify.designMode;
      
      if (hasGSAP && !inEditor) {
        var mm = window.gsap.matchMedia();
        mm.add('(min-width: 1024px)', function () {
          if (motionOK && hasLenis && cfg.smoothScroll !== false) initSmoothScroll();
          initCurtainReveals(false);
          return function () { if (lenis) { lenis.destroy(); lenis = null; } };
        });
        mm.add('(max-width: 1023px)', function () {
          initCurtainReveals(true);
          initMobileSectionMotion();
        });
      }
      
      document.documentElement.classList.add('reveal-ready');
      // DOM surgery must happen before measurement
      initReveal();
      initHero();
      initHeader();
      initMegaMenu();
      initMobileNavAccordion();
      initFeaturedScroll();
      initSearch();
      initCart();
      initSlideshows();
      initBentoVideo();
      
      // if (motionOK && hasST) { initScrubbed(); } // Disabled: causing severe scroll jank
      
      window.nimble.refresh = function () { if (hasST) window.ScrollTrigger.refresh(); if (lenis) lenis.resize(); };
      window.addEventListener('load', window.nimble.refresh);
      document.addEventListener('shopify:section:load', window.nimble.refresh);
      document.addEventListener('shopify:section:unload', window.nimble.refresh);
    } catch (err) {
      revealAll();
      if (window.console) console.warn('[nimble] motion init failed, revealed all content', err);
    }
  }

  function revealAll() {
    document.querySelectorAll('[data-reveal]').forEach(function (el) {
      el.classList.add('is-in');
      el.style.opacity = '1';
      el.style.transform = 'none';
    });
  }

  /* --- Scroll Locks ------------------------------------------------------ */
  var scrollLocks = 0;
  var savedY = 0;
  function lockScroll() {
    if (++scrollLocks === 1) {
      savedY = window.scrollY;
      document.body.style.position = 'fixed';
      document.body.style.top = -savedY + 'px';
      document.body.style.width = '100%';
      document.body.classList.add('no-scroll');
      if (lenis) lenis.stop();
    }
  }
  function unlockScroll() {
    if (scrollLocks > 0 && --scrollLocks === 0) {
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.classList.remove('no-scroll');
      window.scrollTo(0, savedY);
      if (lenis) lenis.start();
    }
  }

  window.nimble.lockScroll = lockScroll;
  window.nimble.unlockScroll = unlockScroll;

  /* --- Smooth scroll (Lenis) --------------------------------------------- */
  var lenis = null;
  function lenisTicker(time) { if (lenis) lenis.raf(time * 1000); }
  function lenisRafFallback(time) { if (lenis) { lenis.raf(time); requestAnimationFrame(lenisRafFallback); } }

  function initSmoothScroll() {
    lenis = new window.Lenis({ duration: 0.6, easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); }, smoothWheel: true, autoRaf: false });
    // Critical wiring (blueprint risk note): share one clock so pins don't drift.
    if (hasST) {
      lenis.on('scroll', window.ScrollTrigger.update);
      window.gsap.ticker.remove(lenisTicker);
      window.gsap.ticker.add(lenisTicker);
      window.gsap.ticker.lagSmoothing(0);
    } else {
      requestAnimationFrame(lenisRafFallback);
    }
    document.documentElement.classList.add('has-lenis');
    // Anchor links (e.g. hero "Our formulation") route through Lenis.
    if (!window.__nimble_lenis_click_bound) {
      window.__nimble_lenis_click_bound = true;
      document.addEventListener('click', function (e) {
        var a = e.target.closest('a[href^="#"]:not([href="#"])');
        if (!a) return;
        var target = document.querySelector(a.getAttribute('href'));
        if (target && lenis) { 
          e.preventDefault(); 
          lenis.scrollTo(target, { offset: -80 }); 
          target.setAttribute('tabindex', '-1');
          target.focus({ preventScroll: true });
        }
      });
    }
  }

  /* --- Reveal on scroll, once -------------------------------------------- */
  function initReveal() {
    var els = Array.prototype.slice.call(document.querySelectorAll('[data-reveal], [data-media-reveal]'));
    if (!els.length) return;

    if (!motionOK) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }

    // Use IntersectionObserver + native CSS transitions for buttery smooth reveals
    // (GSAP ScrollTrigger.batch + CPU staggers were causing scroll lag)
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) { entry.target.classList.add('is-in'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -12% 0px', threshold: 0.05 });
    
    els.forEach(function (el) { io.observe(el); });
  }

  /* --- Hero: poster -> video, scroll cue --------------------------------- */
  function initHero() {
    var hero = document.querySelector('[data-hero]');
    if (!hero) return;
    var video = hero.querySelector('[data-hero-video]');

    // Inject video sources only when motion is allowed and the connection isn't
    // Inject video sources unconditionally
    if (video) {
      var isMobile = window.matchMedia('(max-width: 767px)').matches;
      var raw = video.getAttribute(isMobile ? 'data-sources-mobile' : 'data-sources-desktop')
             || video.getAttribute('data-sources-desktop')
             || video.getAttribute('data-sources-mobile');
      var sources = [];
      try { sources = JSON.parse(raw || '[]'); } catch (e) { sources = []; }
      if (sources.length && !video.querySelector('source')) {
        sources.forEach(function (s) {
          var el = document.createElement('source'); el.src = s.src; if (s.type) el.type = s.type; video.appendChild(el);
        });
        video.addEventListener('playing', function () {
          hero.classList.add('hero--video-ready');
          if (hasGSAP) { window.gsap.fromTo(video, { scale: 1.06 }, { scale: 1, duration: 1.4, ease: GSAP_EASE }); }
        }, { once: true });
        video.load();
        var p = video.play(); if (p && p.catch) { p.catch(function () {}); }
      }
    }

    // Hero copy stagger on load
    if (motionOK && hasGSAP) {
      var items = hero.querySelectorAll('[data-hero-item]');
      if (items.length) {
        window.gsap.set(items, { opacity: 0, y: 32 });
        window.gsap.to(items, { opacity: 1, y: 0, duration: 0.8, ease: GSAP_EASE, stagger: 0.12, delay: 0.15 });
      }
    }

    // Scroll cue kills permanently on first scroll
    var cue = hero.querySelector('[data-scroll-cue]');
    if (cue) {
      var kill = function () { cue.classList.add('is-hidden'); window.removeEventListener('scroll', kill); };
      window.addEventListener('scroll', kill, { passive: true, once: true });
    }
  }

  /* --- Header: mobile nav, sticky, transparent-over-hero ----------------- */
  function initHeader() {
    var header = document.querySelector('[data-header]');
    if (header) {
      // Transparent over hero -> solid on scroll
      var transparent = header.hasAttribute('data-transparent');
      var onScroll = function () {
        var scrolled = window.scrollY > 40;
        header.classList.toggle('is-scrolled', scrolled);
        if (transparent) header.classList.toggle('is-solid', scrolled);
      };
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /* --- Mobile nav accordion (New / Best Sellers / Shop All, incl. nested) - */
  function initMobileNavAccordion() {
    var groups = document.querySelectorAll('[data-accordion-group]');
    if (!groups.length) return;

    function setOpen(item, open) {
      var trigger = item.querySelector(':scope > [data-accordion-trigger]');
      var panel = item.querySelector(':scope > [data-accordion-panel]');
      if (!trigger || !panel) return;
      var icon = trigger.querySelector('.mobile-menu__accordion-icon');

      if (open) {
        panel.hidden = false;
        trigger.setAttribute('aria-expanded', 'true');
        if (icon) icon.textContent = '−';
        requestAnimationFrame(function () { panel.style.maxHeight = panel.scrollHeight + 'px'; });
      } else {
        trigger.setAttribute('aria-expanded', 'false');
        if (icon) icon.textContent = '+';
        panel.style.maxHeight = panel.scrollHeight + 'px';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { panel.style.maxHeight = '0px'; });
        });
      }
    }

    function syncOpenAncestors(panel) {
      var node = panel.parentElement;
      while (node) {
        var ancestorPanel = node.closest('[data-accordion-panel]');
        if (!ancestorPanel) break;
        if (!ancestorPanel.hidden) ancestorPanel.style.maxHeight = ancestorPanel.scrollHeight + 'px';
        node = ancestorPanel.parentElement;
      }
    }

    groups.forEach(function (group) {
      var items = Array.prototype.slice.call(group.children).filter(function (el) {
        return el.matches('[data-accordion-item]');
      });

      items.forEach(function (item) {
        var trigger = item.querySelector(':scope > [data-accordion-trigger]');
        var panel = item.querySelector(':scope > [data-accordion-panel]');
        if (!trigger || !panel) return;

        panel.addEventListener('transitionend', function (e) {
          if (e.propertyName !== 'max-height') return;
          if (trigger.getAttribute('aria-expanded') === 'false') panel.hidden = true;
        });

        trigger.addEventListener('click', function () {
          var willOpen = trigger.getAttribute('aria-expanded') !== 'true';
          items.forEach(function (other) {
            if (other === item) return;
            var otherTrigger = other.querySelector(':scope > [data-accordion-trigger]');
            if (otherTrigger && otherTrigger.getAttribute('aria-expanded') === 'true') setOpen(other, false);
          });
          setOpen(item, willOpen);
          if (willOpen) setTimeout(function () { syncOpenAncestors(panel); }, 20);
        });
      });
    });

    window.addEventListener('resize', function () {
      document.querySelectorAll('[data-accordion-panel]').forEach(function (panel) {
        if (!panel.hidden && panel.style.maxHeight && panel.style.maxHeight !== '0px') {
          panel.style.maxHeight = panel.scrollHeight + 'px';
        }
      });
    });
  }

  /* --- Featured category strip: scroll-position indicator ---------------- */
  function initFeaturedScroll() {
    document.querySelectorAll('[data-featured-scroll]').forEach(function (track) {
      var wrap = track.closest('.mobile-menu__featured');
      var fill = wrap && wrap.querySelector('[data-featured-progress-fill]');
      if (!fill) return;

      function update() {
        var scrollable = track.scrollWidth - track.clientWidth;
        var visibleRatio = track.clientWidth / track.scrollWidth;
        var widthPct = Math.min(Math.max(visibleRatio * 100, 20), 100);
        fill.style.width = widthPct + '%';
        if (scrollable <= 0) { fill.style.left = '0%'; return; }
        var scrolledRatio = track.scrollLeft / scrollable;
        fill.style.left = (scrolledRatio * (100 - widthPct)) + '%';
      }

      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* --- Cart drawer + AJAX add-to-cart ------------------------------------ */
  function initCart() {
    var routes = cfg.routes || {};
    var SECTION = 'cart-drawer';

    function getDrawer() {
      return document.querySelector('[data-cart-drawer]');
    }

    function openDrawer() { 
      var drawerEl = getDrawer();
      if (drawerEl) { 
        drawerEl.classList.add('is-open'); 
        lockScroll(); 
        drawerEl.setAttribute('aria-hidden', 'false'); 
        var c = drawerEl.querySelector('[data-cart-close]'); 
        if (c) c.focus(); 
      } 
    }
    function closeDrawer() { 
      var drawerEl = getDrawer();
      if (drawerEl) { 
        drawerEl.classList.remove('is-open'); 
        unlockScroll(); 
        drawerEl.setAttribute('aria-hidden', 'true'); 
      } 
    }

    document.addEventListener('click', function (e) {
      var closeBtn = e.target.closest('[data-cart-close]');
      var isOverlay = e.target.hasAttribute && e.target.hasAttribute('data-cart-overlay');
      if (closeBtn || isOverlay) closeDrawer();
    });

    document.addEventListener('keydown', function (e) { 
      var drawerEl = getDrawer();
      if (e.key === 'Escape' && drawerEl && drawerEl.classList.contains('is-open')) closeDrawer(); 
    });

    // Delegated click listener for cart icons / links across header & page
    document.addEventListener('click', function (e) {
      var cartBtn = e.target.closest('[data-cart-toggle], a[href="/cart"], .site-header__cart, .mobile-header__cart');
      if (cartBtn) {
        var drawerEl = getDrawer();
        if (drawerEl) {
          e.preventDefault();
          openDrawer();
        }
      }
    });

    // Add to cart delegation (works for all products, dynamic cards, PDP forms)
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-add-to-cart]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-variant-id');
      if (!id) {
        var parentForm = btn.closest('form');
        if (parentForm) {
          var inputId = parentForm.querySelector('[name="id"]');
          if (inputId) id = inputId.value;
        }
      }
      if (!id) return;
      var original = btn.getAttribute('data-label') || btn.textContent;
      btn.setAttribute('data-label', original);
      btn.disabled = true; btn.classList.add('is-loading'); btn.textContent = '…';

      var itemObj = { id: id, quantity: 1 };
      var sellingPlan = btn.getAttribute('data-selling-plan');
      if (sellingPlan) itemObj.selling_plan = parseInt(sellingPlan, 10);

      postCart(routes.cart_add_url + '.js', { items: [itemObj], sections: SECTION, sections_url: window.location.pathname })
        .then(function (data) {
          applySections(data.sections);
          btn.textContent = '✓'; btn.classList.remove('is-loading');
          openDrawer();
          setTimeout(function () { btn.textContent = original; btn.disabled = false; }, 1300);
        })
        .catch(function () { btn.textContent = original; btn.disabled = false; btn.classList.remove('is-loading'); });
    });

    // Buy now on product cards: add the item, then go straight to checkout
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-buy-now]');
      if (!btn) return;
      e.preventDefault();
      var id = btn.getAttribute('data-variant-id');
      if (!id) return;
      var original = btn.getAttribute('data-label') || btn.textContent;
      btn.disabled = true; btn.classList.add('is-loading'); btn.textContent = '…';
      postCart(routes.cart_add_url + '.js', { items: [{ id: id, quantity: 1 }] })
        .then(function () { window.location.href = '/checkout'; })
        .catch(function () { btn.textContent = original; btn.disabled = false; btn.classList.remove('is-loading'); });
    });

    // Intercept native form submissions targeting /cart/add across the homepage & store
    document.addEventListener('submit', function (e) {
      var form = e.target.closest('form[action*="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      
      var submitBtn = form.querySelector('[type="submit"]') || form.querySelector('button');
      var original = submitBtn ? submitBtn.textContent : '';
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.classList.add('is-loading');
        submitBtn.textContent = '…';
      }

      var formData = new FormData(form);
      var variantId = formData.get('id');
      var quantity = formData.get('quantity') || 1;
      var sellingPlan = formData.get('selling_plan');

      if (!variantId) {
        if (submitBtn) { submitBtn.textContent = original; submitBtn.disabled = false; submitBtn.classList.remove('is-loading'); }
        return;
      }

      var itemObj = { id: variantId, quantity: parseInt(quantity, 10) };
      if (sellingPlan) itemObj.selling_plan = parseInt(sellingPlan, 10);

      postCart(routes.cart_add_url + '.js', { items: [itemObj], sections: SECTION, sections_url: window.location.pathname })
        .then(function (data) {
          applySections(data.sections);
          if (submitBtn) {
            submitBtn.textContent = '✓';
            submitBtn.classList.remove('is-loading');
          }
          openDrawer();
          if (submitBtn) {
            setTimeout(function () { submitBtn.textContent = original; submitBtn.disabled = false; }, 1300);
          }
        })
        .catch(function () {
          if (submitBtn) {
            submitBtn.textContent = original;
            submitBtn.disabled = false;
            submitBtn.classList.remove('is-loading');
          }
        });
    });

    // Delegated click listener for cart items (+/- quantity, remove link, upgrade subscription) inside the drawer
    document.addEventListener('click', function (e) {
      var drawerEl = getDrawer();
      if (!drawerEl || !drawerEl.contains(e.target)) return;

      var rm = e.target.closest('[data-cart-remove]');
      if (rm) { e.preventDefault(); changeLine(rm.getAttribute('data-line'), 0); return; }
      
      var step = e.target.closest('[data-qty-step]');
      if (step) {
        e.preventDefault();
        var input = step.parentElement ? step.parentElement.querySelector('input') : null;
        if (input) {
          var val = (parseInt(input.value, 10) || 0) + parseInt(step.getAttribute('data-qty-step'), 10);
          if (val >= 0) changeLine(step.getAttribute('data-line'), val);
        }
        return;
      }
      
      var upgrade = e.target.closest('[data-upgrade-plan]');
      if (upgrade) {
        e.preventDefault();
        var line = upgrade.getAttribute('data-line');
        var planId = upgrade.getAttribute('data-upgrade-plan');
        if (line && planId) {
          drawerEl.classList.add('is-busy');
          postCart(routes.cart_change_url + '.js', { line: parseInt(line, 10), selling_plan: parseInt(planId, 10), sections: SECTION, sections_url: window.location.pathname })
            .then(function (data) { applySections(data.sections); })
            .finally(function () { var d = getDrawer(); if (d) d.classList.remove('is-busy'); });
        }
      }
    });

    document.addEventListener('change', function (e) {
      var drawerEl = getDrawer();
      if (!drawerEl || !drawerEl.contains(e.target)) return;
      var input = e.target.closest('[data-qty-input]');
      if (input) changeLine(input.getAttribute('data-line'), parseInt(input.value, 10) || 0);
    });

    function changeLine(line, qty) {
      if (!line) return;
      var drawerEl = getDrawer();
      if (drawerEl) drawerEl.classList.add('is-busy');
      postCart(routes.cart_change_url + '.js', { line: parseInt(line, 10), quantity: qty, sections: SECTION, sections_url: window.location.pathname })
        .then(function (data) { applySections(data.sections); })
        .finally(function () { var d = getDrawer(); if (d) d.classList.remove('is-busy'); });
    }

    function postCart(url, body) {
      return fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (r) { return r.json(); });
    }

    function applySections(sections) {
      if (!sections || !sections[SECTION]) return;
      var drawerEl = getDrawer();
      var parsed = new DOMParser().parseFromString(sections[SECTION], 'text/html');
      var fresh = parsed.querySelector('[data-cart-contents]');
      var current = drawerEl && drawerEl.querySelector('[data-cart-contents]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;

      /* Footer lives outside [data-cart-contents] - update it too */
      var freshFoot = parsed.querySelector('.cart-drawer__foot');
      var currentFoot = drawerEl && drawerEl.querySelector('.cart-drawer__foot');
      if (freshFoot && currentFoot) {
        currentFoot.innerHTML = freshFoot.innerHTML;
      } else if (freshFoot && !currentFoot && drawerEl) {
        var aside = drawerEl.querySelector('.cart-drawer__panel');
        if (aside) aside.insertAdjacentHTML('beforeend', freshFoot.outerHTML);
      } else if (!freshFoot && currentFoot) {
        currentFoot.remove();
      }
      var count = parsed.querySelector('[data-cart-count]');
      if (count) {
        document.querySelectorAll('[data-cart-count]').forEach(function (el) {
          el.textContent = count.textContent;
          el.classList.toggle('is-empty', count.textContent.trim() === '0');
        });
      }

      // Auto-gift logic
      var giftVariantId = drawerEl ? drawerEl.getAttribute('data-free-gift-variant') : null;
      var totalEl = parsed.querySelector('[data-cart-total]');
      if (giftVariantId && totalEl && drawerEl && !drawerEl.classList.contains('is-updating-gift')) {
         var total = parseInt(totalEl.getAttribute('data-cart-total'), 10);
         var isGiftInCart = !!parsed.querySelector('.cart-drawer__item[data-variant-id="'+giftVariantId+'"][data-is-gift="true"]');
         
         if (total >= 5000 && !isGiftInCart) {
            drawerEl.classList.add('is-updating-gift');
            postCart(routes.cart_add_url + '.js', { 
                items: [{id: giftVariantId, quantity: 1, properties: {'_is_free_gift': 'true'}}], 
                sections: SECTION, 
                sections_url: window.location.pathname 
            })
              .then(function(data) { applySections(data.sections); })
              .finally(function() { var d = getDrawer(); if (d) d.classList.remove('is-updating-gift'); });
         } else if (total < 5000 && isGiftInCart) {
            var lineEl = parsed.querySelector('.cart-drawer__item[data-variant-id="'+giftVariantId+'"][data-is-gift="true"]');
            var line = lineEl ? lineEl.getAttribute('data-line') : null;
            if (line) {
               drawerEl.classList.add('is-updating-gift');
               postCart(routes.cart_change_url + '.js', { 
                   line: parseInt(line, 10), 
                   quantity: 0, 
                   sections: SECTION, 
                   sections_url: window.location.pathname 
               })
                .then(function(data) { applySections(data.sections); })
                .finally(function() { var d = getDrawer(); if (d) d.classList.remove('is-updating-gift'); });
            }
         }
      }
    }

    function refreshCart() {
      var url = routes.cart_url ? routes.cart_url + '.js?sections=' + SECTION : '/cart.js?sections=' + SECTION;
      return fetch(url, { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.sections) applySections(data.sections);
          return data;
        })
        .catch(function () {});
    }

    window.nimble = window.nimble || {};
    window.nimble.cart = {
      open: openDrawer,
      close: closeDrawer,
      apply: applySections,
      refresh: refreshCart,
      section: SECTION
    };

    ['cart:refresh', 'cart:updated', 'ajaxProduct:added'].forEach(function (evtName) {
      document.addEventListener(evtName, function () { refreshCart(); });
    });
  }

  /* --- Horizontal rails (bestsellers, bento mobile) ---------------------- */
  function initSlideshows() {
    document.querySelectorAll('[data-rail]').forEach(function (rail) {
      var track = rail.querySelector('[data-rail-track]');
      if (!track) return;
      var prev = rail.querySelector('[data-rail-prev]');
      var next = rail.querySelector('[data-rail-next]');
      var live = rail.querySelector('[data-rail-live]');

      function cardWidth() {
        var card = track.firstElementChild;
        if (!card) return 300;
        var gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap || 0) || 0;
        return card.getBoundingClientRect().width + gap;
      }
      function scrollByCard(dir) { track.scrollBy({ left: dir * cardWidth(), behavior: reduceMotion ? 'auto' : 'smooth' }); }
      if (prev) prev.addEventListener('click', function () { scrollByCard(-1); });
      if (next) next.addEventListener('click', function () { scrollByCard(1); });

      function updateArrows() {
        var max = track.scrollWidth - track.clientWidth - 2;
        if (prev) prev.disabled = track.scrollLeft <= 2;
        if (next) next.disabled = track.scrollLeft >= max;
        if (live) { var i = Math.round(track.scrollLeft / cardWidth()) + 1; live.textContent = 'Item ' + i; }
      }
      track.addEventListener('scroll', function () { window.requestAnimationFrame(updateArrows); }, { passive: true });
      updateArrows();

      // Pointer drag on desktop
      var down = false, startX = 0, startLeft = 0;
      track.addEventListener('pointerdown', function (e) { if (e.pointerType === 'mouse') { down = true; startX = e.clientX; startLeft = track.scrollLeft; track.classList.add('is-dragging'); } });
      window.addEventListener('pointermove', function (e) { if (down) { track.scrollLeft = startLeft - (e.clientX - startX); } });
      window.addEventListener('pointerup', function () { down = false; track.classList.remove('is-dragging'); });
    });
  }

  /* --- Bento video tiles: hover (desktop) / intersection (mobile) -------- */
  function initBentoVideo() {
    var tiles = document.querySelectorAll('[data-tile-video]');
    if (!tiles.length) return;
    var canHover = window.matchMedia('(hover: hover)').matches;

    tiles.forEach(function (video) {
      if (canHover) {
        var card = video.closest('a, li') || video.parentElement;
        card.addEventListener('mouseenter', function () { safePlay(video); });
        card.addEventListener('mouseleave', function () { video.pause(); });
      }
    });

    if (!canHover && 'IntersectionObserver' in window && !saveData && !reduceMotion) {
      var activeTiles = new Map();
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) { 
          if (entry.isIntersecting) { activeTiles.set(entry.target, entry.intersectionRatio); } 
          else { activeTiles.delete(entry.target); entry.target.pause(); } 
        });
        
        var maxRatio = 0; var bestVideo = null;
        activeTiles.forEach(function(ratio, video) {
          if (ratio > maxRatio) { maxRatio = ratio; bestVideo = video; }
        });
        
        activeTiles.forEach(function(ratio, video) {
          if (video === bestVideo) safePlay(video);
          else video.pause();
        });
      }, { threshold: [0.1, 0.3, 0.5, 0.7, 0.9] });
      tiles.forEach(function (v) { io.observe(v); });
    }
    function safePlay(v) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
  }

  /* --- Scrubbed / pinned effects (GSAP only) ----------------------------- */
  function initScrubbed() {
    // Split-banner image zoom 1.1 -> 1.0 across the section scroll
    document.querySelectorAll('[data-scrub-zoom]').forEach(function (img) {
      window.gsap.fromTo(img, { scale: 1.12 }, {
        scale: 1, ease: 'none',
        scrollTrigger: { trigger: img.closest('[data-split]') || img, start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });

    // Full banner parallax
    document.querySelectorAll('.full-banner__img').forEach(function (img) {
      window.gsap.fromTo(img, { yPercent: -10, scale: 1.1 }, {
        yPercent: 10, scale: 1.1, ease: 'none',
        scrollTrigger: { trigger: img.closest('.full-banner'), start: 'top bottom', end: 'bottom top', scrub: true }
      });
    });
  }

  /* --- Curtain: pin preceding section so cards slide up over it --------- */
  var CURTAIN_SKIP = '.nhero, .trust-strip, .usp-strip, .ugc';

  function initCurtainReveals(isMobile) {
    if (!hasST) return;  /* curtain is structural, runs even with motionOK=false */

    /* Phones / tablets: pinning made the page jump and judder while scrolling,
       so the sections simply scroll normally there. */
    if (isMobile) return;

    /* ── 1. Find every "cover" section ────────────────────────────────── */
    var coverEls = document.querySelectorAll('.bestsellers');

    if (window.console) console.log('[curtain] cover sections found:', coverEls.length, coverEls);

    var pairIndex  = 0;
    var pinnedSet  = new Set();

    coverEls.forEach(function (coverInner, i) {
      /* Skip sections explicitly opted out */
      if (coverInner.hasAttribute('data-no-curtain')) {
        if (window.console) console.log('[curtain] skip (data-no-curtain):', coverInner);
        return;
      }

      /* ── 2. Walk to the .shopify-section wrappers ───────────────────── */
      var coverWrap = coverInner.closest('.shopify-section');
      if (!coverWrap) return;

      var prevWrap = coverWrap.previousElementSibling;
      while (prevWrap && !prevWrap.classList.contains('shopify-section')) {
        prevWrap = prevWrap.previousElementSibling;
      }
      if (!prevWrap) {
        if (window.console) console.log('[curtain] skip (no previous .shopify-section):', coverInner);
        return;
      }

      /* ── 3. Check the skip list ─────────────────────────────────────── */
      if (prevWrap.querySelector(CURTAIN_SKIP)) {
        if (window.console) console.log('[curtain] skip (hero/trust/usp):', prevWrap);
        return;
      }

      /* ── 4. Don't double-pin ────────────────────────────────────────── */
      if (pinnedSet.has(prevWrap)) return;

      /* ── 5. Pin target = inner <section>, NOT the .shopify-section ──── */
      var pinTarget = prevWrap.querySelector('section')
                   || prevWrap.firstElementChild
                   || prevWrap;

      /* A pinned section parks at the bottom of the screen. If it is shorter
         than the viewport that leaves an empty white band above it while the
         cards slide over, so only pin sections that fill the screen. */
      if (pinTarget.offsetHeight < window.innerHeight) {
        if (window.console) console.log('[curtain] skip (section shorter than viewport):', pinTarget);
        return;
      }
      pinnedSet.add(prevWrap);

      /* ── 6. Add data attributes for targeting ───────────────────────── */
      prevWrap.setAttribute('data-curtain-pinned', '');
      coverWrap.setAttribute('data-curtain-cover', '');

      /* Ensure cover has an opaque background */
      var coverBg = window.getComputedStyle(coverInner).backgroundColor;
      if (!coverBg || coverBg === 'rgba(0, 0, 0, 0)' || coverBg === 'transparent') {
        coverInner.style.backgroundColor = 'var(--cream, #F7F4EF)';
      }

      if (window.console) console.log('[curtain] PINNING | pinTarget:', pinTarget, '| cover:', coverInner);

      /* ── 7. Create the ScrollTrigger pin ────────────────────────────── */
      window.ScrollTrigger.create({
        trigger:       pinTarget,
        start:         isMobile ? 'bottom 80%' : 'bottom bottom',
        end:           () => "+=" + coverWrap.offsetHeight,
        pin:           true,
        pinSpacing:    false,
        anticipatePin: 1,
        id:            'curtain-pin-' + pairIndex
      });
    });

    /* Lazy images change the page height after the pin was measured, which
       left the pin start/end stale and made the scroll jump. Re-measure once
       they have loaded. */
    if (pinnedSet.size) {
      var refreshTimer;
      var queueRefresh = function () {
        clearTimeout(refreshTimer);
        refreshTimer = setTimeout(function () { window.ScrollTrigger.refresh(); }, 200);
      };
      document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        if (!img.complete) img.addEventListener('load', queueRefresh, { once: true });
      });
    }

    /* Keep header above everything */
    var headerWrapper = document.querySelector('.shopify-section-group-header-group');
    if (headerWrapper) {
      headerWrapper.style.zIndex = 100;
    }
  }

  /* --- Mobile Section Motion (Alternative to pinning) ------------------ */
  function initMobileSectionMotion() {
    // Disabled: this was adding a scrubbed y-translation to every section on mobile,
    // which caused significant scroll jank. 
    return;
  }
  
  /* --- Mega Menu GSAP Switching ------------------------------------------- */
  function initMegaMenu() {
    var tabs = document.querySelectorAll('[data-mega-tab]');
    if (!tabs.length || !hasGSAP) return;
    var isAnimating = false;

    tabs.forEach(function (tab) {
      tab.addEventListener('mouseenter', function () {
        if (tab.classList.contains('is-active') || isAnimating) return;
        var targetId = tab.getAttribute('data-mega-tab');
        var megaInner = tab.closest('.mega-menu__inner');
        var oldTab = megaInner.querySelector('.mega-menu__tab.is-active');
        var oldPanel = megaInner.querySelector('.mega-menu__panel.is-active');
        var newPanel = megaInner.querySelector('.mega-menu__panel[data-mega-panel="' + targetId + '"]');
        if (!newPanel || !oldPanel) return;

        isAnimating = true;
        if (oldTab) oldTab.classList.remove('is-active');
        tab.classList.add('is-active');

        oldPanel.style.position = 'absolute';
        oldPanel.style.top = '0';
        oldPanel.style.left = '0';
        oldPanel.style.width = '100%';
        
        newPanel.style.display = 'block';
        newPanel.style.position = 'relative';

        window.gsap.to(oldPanel, { opacity: 0, y: 15, duration: 0.35, ease: 'power3.out' });
        window.gsap.fromTo(newPanel, { opacity: 0, y: 15 }, {
          opacity: 1, y: 0, duration: 0.35, ease: 'power3.out',
          onComplete: function() {
            oldPanel.classList.remove('is-active');
            oldPanel.style.display = 'none';
            oldPanel.style.position = '';
            newPanel.classList.add('is-active');
            isAnimating = false;
          }
        });
      });
    });

  }

  /* --- Predictive Search ------------------------------------------------- */
  /* Mirrors snippets/product-title-short.liquid - keep both in sync. */
  function shortenProductTitle(title) {
    var t = (title || '').toLowerCase();
    if (t.indexOf('sheet') !== -1) return 'Laundry Detergent Sheets';
    if (t.indexOf('fabric') !== -1 && (t.indexOf('condition') !== -1 || t.indexOf('soften') !== -1)) return 'Fabric Softener';
    if (t.indexOf('lift off') !== -1 || t.indexOf('lift-off') !== -1) return (t.indexOf('50ml') !== -1 || t.indexOf('50 ml') !== -1 || t.indexOf('on-the-go') !== -1) ? 'Lift Off On-the-go' : 'Lift Off Power Spray';
    if (t.indexOf('multi') !== -1 && t.indexOf('purpose') !== -1) return 'Multi Purpose Power Cleaner';
    if (t.indexOf('antibacterial') !== -1 && t.indexOf('cleaner') !== -1) return 'Multi Purpose Power Cleaner';
    if (t.indexOf('sanitiser') !== -1 || t.indexOf('sanitizer') !== -1) return 'Moisturising Hand Sanitiser';
    if (t.indexOf('laundry') !== -1 && t.indexOf('detergent') !== -1) return 'Laundry Detergent';
    return title;
  }

  function initSearch() {
    var toggle = document.querySelector('[data-search-toggle]');
    var overlay = document.querySelector('[data-search-overlay]');
    if (!toggle || !overlay) return;
    
    var close = overlay.querySelector('[data-search-close]');
    var input = overlay.querySelector('[data-search-input]');
    var resultsBox = overlay.querySelector('[data-search-results]');
    var suggestions = overlay.querySelector('[data-search-suggestions]');
    
    function openSearch() {
      overlay.classList.add('is-active');
      lockScroll();
      setTimeout(function() { input.focus(); }, 100);
    }
    function closeSearch() {
      overlay.classList.remove('is-active');
      unlockScroll();
    }
    
    toggle.addEventListener('click', function(e) { e.preventDefault(); openSearch(); });
    if (close) close.addEventListener('click', closeSearch);
    document.addEventListener('keydown', function(e) { if (e.key === 'Escape' && overlay.classList.contains('is-active')) closeSearch(); });
    
    overlay.querySelectorAll('[data-search-term]').forEach(function(btn) {
      btn.addEventListener('click', function() {
        input.value = btn.getAttribute('data-search-term');
        input.dispatchEvent(new Event('input'));
      });
    });
    
    var timer;
    input.addEventListener('input', function() {
      clearTimeout(timer);
      var q = input.value.trim();
      if (!q) {
        suggestions.style.display = '';
        resultsBox.innerHTML = '';
        return;
      }
      timer = setTimeout(function() {
        fetch('/search/suggest.json?q=' + encodeURIComponent(q) + '&resources[type]=product&resources[limit]=5')
          .then(function(r) { return r.json(); })
          .then(function(data) {
            suggestions.style.display = 'none';
            var products = data.resources.results.products;
            if (!products || !products.length) {
              resultsBox.innerHTML = '<p>No results found.</p>';
              return;
            }
            var html = '';
            products.forEach(function(p) {
              html += '<a href="' + p.url + '" class="search-result-item" style="display:flex; flex-direction:column; gap:12px; font-family:var(--font-heading); color:inherit; text-decoration:none;">';
              if (p.image) { html += '<img src="' + p.image + '&width=300" style="width:100%; aspect-ratio:4/5; object-fit:contain; background:#FFFFFF; border-radius:16px; mix-blend-mode:multiply; border: 1px solid rgba(0,0,0,0.04);">'; }
              html += '<span style="font-size:1.1rem; text-transform:uppercase; font-weight:800;">' + shortenProductTitle(p.title) + '</span></a>';
            });
            resultsBox.innerHTML = html;
          });
      }, 150);
    });
  }
})();
